'use client'

import { DehydratedState, HydrationBoundary } from '@tanstack/react-query'

import { LayoutBreadcrumbsOverride } from '@/app/layout/MainSiteLayout/LayoutBreadcrumbsOverride'

import { ProductScreen, useProductPagePushEvents } from '@/screens/Product'
import { ProductSsrData, ProductSsrDataProvider } from '@/screens/Product/context/product-ssr-data-context'
import { useProductPageMeta } from '@/screens/Product/hooks/use-product-page-meta'
import { useProduct } from '@/screens/Product/hooks/useProduct'

import { useInitGlobalAddToCart } from '@/features/cart/init-global-events'
import { useProductReviewSchemaPreview } from '@/features/product-reviews'

import { CAProductPageScript } from '@/shared/integrations/city-ads'
import { ProductSchemaNode } from '@/shared/lib/seo'
import { TPage } from '@/shared/types/deprecated-types'

type Props = {
	dehydratedState: DehydratedState
	breadcrumbs: TPage[]
	ssrProductData?: ProductSsrData | null
}

const ProductPageContent = () => {
	const { product } = useProduct()
	const { categoryName } = useProductPageMeta(product)
	const reviewsPreview = useProductReviewSchemaPreview(product?.KOD)

	useProductPagePushEvents(product)
	useInitGlobalAddToCart()

	if (!product) return null

	return (
		<>
			<ProductSchemaNode data={product} categoryName={categoryName} reviews={reviewsPreview} />
			<CAProductPageScript productId={product.ID} />
			<ProductScreen />
		</>
	)
}

export const ProductClient = ({ dehydratedState, breadcrumbs, ssrProductData }: Props) => {
	return (
		<>
			<LayoutBreadcrumbsOverride pages={breadcrumbs} />
			<HydrationBoundary state={dehydratedState}>
				{/* Server-prefetched product data — read synchronously by the consuming hooks on both the
				    server and the client's first render, so the tree renders identically (no hydration
				    mismatch), the content is in the SSR HTML, and the hooks skip their client request. */}
				<ProductSsrDataProvider value={ssrProductData ?? null}>
					<ProductPageContent />
				</ProductSsrDataProvider>
			</HydrationBoundary>
		</>
	)
}
